import {
  CustomersSelectQuery,
  CustomersSelectQueryVariables,
} from "@/gql/graphql";
import { OptionItem } from "@/types/OptionItem";
import { useApolloClient } from "@apollo/client";
import { useDebounce } from "@stenajs-webui/core";
import { AsyncMultiSelect } from "@stenajs-webui/select";
import debounce from "lodash/debounce";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { MultiValue } from "react-select";
import { customersSelectQuery } from "./CustomerSelect";

interface Props {
  value: OptionItem[];
  onValueChange: (value: OptionItem[]) => void;
  placeholder?: string;
  isClearable?: boolean;
}

export const CustomerMultiSelect: React.FC<Props> = ({
  value,
  onValueChange,
  placeholder,
  isClearable,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const debouncedInputValue = useDebounce(inputValue, 400);
  const client = useApolloClient();

  const loadOptions = useMemo(
    () =>
      debounce((input: string, callback: (options: OptionItem[]) => void) => {
        if (input.length < 2) {
          callback([]);
          return;
        }
        client
          .query<CustomersSelectQuery, CustomersSelectQueryVariables>({
            query: customersSelectQuery,
            variables: { query: input },
          })
          .then((result) => {
            callback(
              result.data?.productPrice.customers.byCustomerNumberOrIndex.map(
                (customer) => ({
                  label: `${customer.custNo} (${customer.custIndex})`,
                  value: customer.id,
                })
              ) ?? []
            );
          })
          .catch(() => callback([]));
      }, 400),
    [client]
  );

  useEffect(() => () => loadOptions.cancel(), [loadOptions]);

  return (
    <AsyncMultiSelect<OptionItem>
      value={value}
      onInputChange={setInputValue}
      loadOptions={loadOptions}
      onChange={(v: MultiValue<OptionItem>) => onValueChange([...v])}
      placeholder={
        placeholder ?? "Search for customer number or customer index"
      }
      noOptionsMessage={({ inputValue }: { inputValue: string }) => {
        if (inputValue.length === 0) {
          return "Search for customer number or customer index";
        }
        if (inputValue.length < 2 || debouncedInputValue !== inputValue) {
          return "Type at least 2 characters to search";
        }
        return "No customers found";
      }}
      isClearable={isClearable}
    />
  );
};
